// components/EscapeRoom.jsx
import React, { useState, useEffect, useMemo } from 'react';
import locksData from '../data/locksData'; // import all stations data (including bonus)
import { logProgress, updatePresence, fetchStationPresence } from '../firebase';

// Helper to shuffle an array
const shuffleArray = (array) => {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const EscapeRoom = () => {
  const [stations, setStations] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [completed, setCompleted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [showBonus, setShowBonus] = useState(false);
  const [bonusStation, setBonusStation] = useState(null);
  const [incorrectCount, setIncorrectCount] = useState(0);
  const [studentFirstName, setStudentFirstName] = useState('');
  const [studentLastInitial, setStudentLastInitial] = useState('');
  const [sessionId] = useState(() => Math.random().toString(36).slice(2, 10));
  const [studentInfoSubmitted, setStudentInfoSubmitted] = useState(false);
  const [classmates, setClassmates] = useState([]);
  const [forceVariantOne, setForceVariantOne] = useState(false);
  const [questionsCompleted, setQuestionsCompleted] = useState(0);

  const initializeStations = () => {
    // Separate the bonus station (lock8) from regular stations
    const regularStations = locksData.filter((station) => station.id !== 'lock8');
    const bonus = locksData.find((station) => station.id === 'lock8');

    // Randomize regular station order
    const randomizedStations = shuffleArray(regularStations).map((station) => {
      // Pick a question variant for each station
      const variant = forceVariantOne
        ? station.variants[0]
        : station.variants[Math.floor(Math.random() * station.variants.length)];
      return { ...station, variant };
    });

    // Prepare bonus station with chosen variant
    if (bonus) {
      const bonusVariant = forceVariantOne
        ? bonus.variants[0]
        : bonus.variants[Math.floor(Math.random() * bonus.variants.length)];
      setBonusStation({ ...bonus, variant: bonusVariant });
    } else {
      setBonusStation(null);
    }

    setStations(randomizedStations);
    setCurrentQuestion(randomizedStations[0]?.variant || null);
    setCurrentIndex(0);
    setIncorrectCount(0);
    setCompleted(false);
    setShowBonus(false);
    setQuestionsCompleted(0);
  };

  // Initial session + station setup
  useEffect(() => {
    initializeStations();
    // Fire-and-forget log when a new session starts
    logProgress({
      type: 'session_start',
      sessionId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Secret teacher combo: Ctrl+Shift+1 toggles "variant 1 only" mode
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === '1') {
        setForceVariantOne((prev) => {
          const next = !prev;
          alert(
            next
              ? 'Variant 1 mode ON: all questions will use Variant 1.'
              : 'Variant 1 mode OFF: questions will use random variants.'
          );
          return next;
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // When variant mode changes, reinitialize stations so it takes effect
  useEffect(() => {
    if (!stations.length) return;
    initializeStations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceVariantOne]);

  const handleStudentInfoSubmit = () => {
    if (!studentFirstName.trim() || !studentLastInitial.trim()) {
      alert('Please enter your first name and last initial to begin.');
      return;
    }

    setStudentInfoSubmitted(true);
    logProgress({
      type: 'student_info_submitted',
      sessionId,
      studentFirstName: studentFirstName.trim(),
      studentLastInitial: studentLastInitial.trim().toUpperCase(),
    });
  };

  const handleSubmit = () => {
    if (userAnswer.trim() === '') return alert('Please enter an answer!');
    if (!currentQuestion) return;

    const correct = userAnswer.trim().toUpperCase() === currentQuestion.answer;
    const nextQuestionsCompleted = correct ? questionsCompleted + 1 : questionsCompleted;

    // Log every attempt (quick and dirty, fire-and-forget)
    logProgress({
      type: 'answer_attempt',
      sessionId,
      studentFirstName: studentFirstName.trim(),
      studentLastInitial: studentLastInitial.trim().toUpperCase(),
      stationId: showBonus ? bonusStation?.id : stations[currentIndex]?.id,
      isBonus: showBonus,
      stationIndex: currentIndex,
      answerGiven: userAnswer.trim(),
      isCorrect: correct,
      strikesBefore: incorrectCount,
      questionsCompletedBefore: questionsCompleted,
    });

    if (correct) {
      alert('✅ Correct!');
      setUserAnswer('');
      setIncorrectCount(0); // Reset incorrect count on correct answer
      setQuestionsCompleted(nextQuestionsCompleted);

      if (showBonus) {
        // Completed bonus challenge
        setCompleted(true);
        logProgress({
          type: 'bonus_completed',
          sessionId,
          studentFirstName: studentFirstName.trim(),
          studentLastInitial: studentLastInitial.trim().toUpperCase(),
          questionsCompleted: nextQuestionsCompleted,
        });
      } else if (currentIndex + 1 < stations.length) {
        const nextIndex = currentIndex + 1;
        setCurrentIndex(nextIndex);
        setCurrentQuestion(stations[nextIndex].variant);
      } else {
        // Completed all regular stations - offer bonus
        setCompleted(true);
        logProgress({
          type: 'stations_completed',
          sessionId,
          studentFirstName: studentFirstName.trim(),
          studentLastInitial: studentLastInitial.trim().toUpperCase(),
          questionsCompleted: nextQuestionsCompleted,
        });
      }
    } else {
      const newIncorrectCount = incorrectCount + 1;
      setIncorrectCount(newIncorrectCount);

      if (newIncorrectCount >= 3) {
        alert('❌ Incorrect. That was your 3rd wrong answer. Your progress has been reset! 😱');
        logProgress({
          type: 'reset_after_3_incorrect',
          sessionId,
          studentFirstName: studentFirstName.trim(),
          studentLastInitial: studentLastInitial.trim().toUpperCase(),
        });
        initializeStations(); // Reset everything
      } else {
        alert(`❌ Incorrect. Try again. (${newIncorrectCount}/3 strikes)`);
      }
      setUserAnswer('');
    }
  };

  const handleBonusAccept = () => {
    if (!bonusStation) return;
    setShowBonus(true);
    setCompleted(false);
    setIncorrectCount(0); // Reset strikes for bonus challenge
    setCurrentQuestion(bonusStation.variant);
    logProgress({
      type: 'bonus_started',
      sessionId,
      studentFirstName: studentFirstName.trim(),
      studentLastInitial: studentLastInitial.trim().toUpperCase(),
    });
  };

  const handleBonusDecline = () => {
    setShowBonus(false);
    setCompleted(true);
    logProgress({
      type: 'bonus_declined',
      sessionId,
      studentFirstName: studentFirstName.trim(),
      studentLastInitial: studentLastInitial.trim().toUpperCase(),
    });
  };

  const currentStation = showBonus ? bonusStation : stations[currentIndex];
  const stationProgress = showBonus ? 'BONUS CHALLENGE' : `Station ${currentIndex + 1} of ${stations.length}`;

  const currentStationId = currentStation?.id;
  const stationKey = useMemo(
    () => (currentStationId ? `${currentStationId}${showBonus ? '_bonus' : ''}` : null),
    [currentStationId, showBonus]
  );

  // Keep our presence up to date whenever student info + station are known
  useEffect(() => {
    if (!studentInfoSubmitted || !stationKey || !studentFirstName.trim() || !studentLastInitial.trim()) return;

    updatePresence({
      sessionId,
      stationKey,
      stationId: currentStationId,
      isBonus: showBonus,
      studentFirstName: studentFirstName.trim(),
      studentLastInitial: studentLastInitial.trim().toUpperCase(),
    });
  }, [
    studentInfoSubmitted,
    stationKey,
    currentStationId,
    showBonus,
    sessionId,
    studentFirstName,
    studentLastInitial,
  ]);

  // Poll classmates on the same station every ~1s
  useEffect(() => {
    if (!studentInfoSubmitted || !stationKey) return undefined;

    let isCancelled = false;

    const poll = async () => {
      try {
        const people = await fetchStationPresence(stationKey);
        if (isCancelled) return;
        const others = people.filter((p) => p.sessionId !== sessionId);
        setClassmates(others);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching classmates', err);
      }
    };

    // initial fetch immediately, then every second
    poll();
    const intervalId = setInterval(poll, 1000);

    return () => {
      isCancelled = true;
      clearInterval(intervalId);
    };
  }, [studentInfoSubmitted, stationKey, sessionId]);

  if (!stations.length) return <div>Loading stations...</div>;

  if (completed && !showBonus && bonusStation) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>🎉 Congratulations! You completed all 7 stations!</h2>
        <p style={{ fontSize: '1.2rem', margin: '2rem 0' }}>
          You've unlocked the BONUS CHALLENGE for a bonus prize! This is much harder.
        </p>
        <p style={{ fontSize: '1rem', marginBottom: '2rem' }}>
          Do you want to attempt the {bonusStation.color} {bonusStation.title}?
        </p>
        <button
          onClick={handleBonusAccept}
          style={{ padding: '1rem 2rem', fontSize: '1.2rem', marginRight: '1rem', cursor: 'pointer' }}
        >
          Yes, I'm ready! 💪
        </button>
        <button
          onClick={handleBonusDecline}
          style={{ padding: '1rem 2rem', fontSize: '1.2rem', cursor: 'pointer' }}
        >
          No thanks, I'm done 😊
        </button>
      </div>
    );
  }

  if (completed && showBonus) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>🏆 INCREDIBLE! You conquered the BONUS CHALLENGE!</h2>
        <p style={{ fontSize: '1.2rem', marginTop: '1rem' }}>You are an algebra master! 🎓</p>
      </div>
    );
  }

  if (completed) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>🎉 Congratulations! You completed the Escape Room!</h2>
      </div>
    );
  }

  // Layout: nice centered card over a soft gradient background
  if (!studentInfoSubmitted) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #6dd5ed 100%)',
          padding: '1rem',
        }}
      >
        <div
          style={{
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 12px 30px rgba(0,0,0,0.2)',
            padding: '2.5rem 2rem',
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
          }}
        >
          <h1 style={{ marginBottom: '0.5rem' }}>Algebra Escape Room</h1>
          <p style={{ marginBottom: '1.5rem', fontSize: '1.05rem', color: '#444' }}>
            Enter your first name and last initial to begin your mission.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
            <input
              type="text"
              value={studentFirstName}
              onChange={(e) => setStudentFirstName(e.target.value)}
              placeholder="First name"
              style={{
                padding: '0.6rem 0.8rem',
                fontSize: '1rem',
                marginRight: '0.5rem',
                borderRadius: '8px',
                border: '1px solid #ccc',
                flex: 1,
                maxWidth: '220px',
              }}
            />
            <input
              type="text"
              maxLength={1}
              value={studentLastInitial}
              onChange={(e) => setStudentLastInitial(e.target.value)}
              placeholder="Last initial"
              style={{
                padding: '0.6rem 0.8rem',
                fontSize: '1rem',
                width: '90px',
                borderRadius: '8px',
                border: '1px solid #ccc',
                textTransform: 'uppercase',
              }}
            />
          </div>
          <button
            onClick={handleStudentInfoSubmit}
            style={{
              background: '#1e88e5',
              color: 'white',
              border: 'none',
              borderRadius: '999px',
              padding: '0.8rem 2.2rem',
              fontSize: '1.05rem',
              cursor: 'pointer',
              boxShadow: '0 6px 14px rgba(30,136,229,0.4)',
            }}
          >
            Start Escape Room
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #6dd5ed 100%)',
        padding: '1rem',
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 12px 30px rgba(0,0,0,0.2)',
          padding: '2rem 2.5rem',
          maxWidth: '700px',
          width: '100%',
        }}
      >
        <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0 }}>
              {currentStation?.color} {currentStation?.title}
            </h2>
            {showBonus && <p style={{ color: 'red', fontWeight: 'bold', marginTop: '0.25rem' }}>BONUS CHALLENGE</p>}
          </div>
          <div style={{ textAlign: 'right', fontSize: '0.95rem', color: '#555' }}>
            <div>
              {studentFirstName} {studentLastInitial.toUpperCase()}.
            </div>
            <div style={{ marginTop: '0.25rem' }}>{stationProgress}</div>
          </div>
        </div>

        <div style={{ margin: '1.5rem 0' }}>
          <p style={{ fontSize: '1.05rem' }}>
            <strong>Question:</strong> {currentQuestion?.prompt}
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
          <input
            type="text"
            value={userAnswer}
            onChange={(e) => setUserAnswer(e.target.value)}
            placeholder="Enter your answer here"
            style={{
              padding: '0.6rem 0.8rem',
              fontSize: '1rem',
              flex: 1,
              borderRadius: '8px',
              border: '1px solid #ccc',
            }}
          />
          <button
            onClick={handleSubmit}
            style={{
              marginLeft: '0.75rem',
              padding: '0.6rem 1.5rem',
              fontSize: '1rem',
              borderRadius: '999px',
              border: 'none',
              background: '#43a047',
              color: 'white',
              cursor: 'pointer',
              boxShadow: '0 4px 10px rgba(67,160,71,0.4)',
            }}
          >
            Submit
          </button>
        </div>

        {incorrectCount > 0 && (
          <p style={{ color: 'orange', fontWeight: 'bold', marginTop: '0.5rem' }}>
            ⚠️ Strikes: {incorrectCount}/3
          </p>
        )}

        {classmates.length > 0 && (
          <div style={{ marginTop: '1rem', fontSize: '0.95rem', color: '#555' }}>
            <strong>Also working on this station:</strong>{' '}
            {classmates
              .map(
                (c) =>
                  `${c.studentFirstName || 'Student'} ${
                    c.studentLastInitial ? c.studentLastInitial.toUpperCase() + '.' : ''
                  }`
              )
              .join(', ')}
          </div>
        )}
      </div>
    </div>
  );
};

export default EscapeRoom;

